import 'react-toastify/dist/ReactToastify.css'
import React, { useState, useEffect } from 'react'
import { ToastContainer, toast } from 'react-toastify'
import Button from '@atlaskit/button'
import DynamicTable from '@atlaskit/dynamic-table'
import PageHeader from '@atlaskit/page-header'
import { RadioGroup } from '@atlaskit/radio'
import SectionMessage from '@atlaskit/section-message'

import {
  fetchAllProjects,
  fetchProjectEnabledSettings,
  updateDisabledProjects,
  recordOpenEvent,
  recordSaveEvent,
  recordSaveFailedEvent,
  fetchLegacySettings,
  deleteLegacySettings
} from './adminHelper'

const ProjectEnabler = ({ onChange, value, unsaved }) => {
  if (!value) {
    return <div style={{ padding: 10 }}>Loading...</div>
  }
  
  if (value === 'error') {
    return <div style={{ padding: 10 }}>Something went wrong</div>
  }

  const options = [
    { name: 'enabled', value: 'enabled', label: 'Enabled' },
    { name: 'disabled', value: 'disabled', label: 'Disabled' }
  ]

  const radioStyle = unsaved ? { backgroundColor: 'lightgray', borderRadius: '5px' } : {}

  return <div style={radioStyle}><RadioGroup options={options} onChange={onChange} value={value} /></div>
}

const ProjectNameDisplay = ({ project }) =>
  <>
    <img alt='' style={{ height: 24, width: 24, marginRight: 5, marginBottom: 5 }} src={project.avatarUrls['48x48']} />
    <span>{project.name} ({project.key})</span>
  </>

const Admin = () => {
  const [projects, setProjects] = useState([])
  const [savedProjectSettings, setSavedProjectSettings] = useState({})
  const [localProjectSettings, setLocalProjectSettings] = useState({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [visibleProjects, setVisibleProjects] = useState([])
  const [unsavedChanges, setUnsavedChanges] = useState([])
  const [saving, setSaving] = useState(false)
  const [legacySettings, setLegacySettings] = useState(null)
  const [legacySettingsImported, setLegacySettingsImported] = useState(false)

  useEffect(() => {
    async function load () {
      setLoading(true)
      let allProjects

      try {
        allProjects = await fetchAllProjects()
        if (allProjects === null) {
          setError(true)
        } else {
          setProjects(allProjects)
        }
      } catch (error) {
        setError(true)
      }

      const legacySettings = await fetchLegacySettings()
      if (legacySettings) {
        setLegacySettings(legacySettings)
      }

      setLoading(false)
      await recordOpenEvent(allProjects.length)
    }

    load()
  }, [])

  useEffect(() => {
    const unsavedProjects = Object.keys(localProjectSettings).filter(projectKey => localProjectSettings[projectKey] && localProjectSettings[projectKey] !== savedProjectSettings[projectKey])
    setUnsavedChanges(unsavedProjects)
  }, [localProjectSettings, savedProjectSettings])

  const toggleProject = (key, value) => {
    setLocalProjectSettings({
      ...localProjectSettings,
      [key]: value
    })
  }

  const handleSave = async () => {
    const changesToSave = unsavedChanges.length
    setSaving(true)
    const result = await updateDisabledProjects(localProjectSettings)

    if (result) {
      toast('Settings saved', { type: toast.TYPE.INFO })
      setSavedProjectSettings({
        ...savedProjectSettings,
        ...localProjectSettings
      })
      setLocalProjectSettings({})

      if (legacySettingsImported) {
        await deleteLegacySettings()
        setLegacySettings(null)
        setLegacySettingsImported(false)
      }

      recordSaveEvent(changesToSave)
    } else {
      toast('Failed to save settings', { type: toast.TYPE.ERROR })
      recordSaveFailedEvent()
    }

    setSaving(false)
  }

  const onPageRowsChange = async (pageRows) => {
    const projectKeys = pageRows.map(row => row.cells[0].key.substring('project-'.length))

    if (projectKeys.some(projectKey => !savedProjectSettings.hasOwnProperty(projectKey))) {
      const projectsEnabledSettings = await fetchProjectEnabledSettings(projects.filter(project => projectKeys.includes(project.key)))
      setSavedProjectSettings(currentDisabledProjects => {
        const newDisabledProjects = {}

        for (const projectKey of projectKeys) {
          newDisabledProjects[projectKey] = projectsEnabledSettings[projectKey]
        }

        return {
          ...currentDisabledProjects,
          ...newDisabledProjects
        }
      })
    }

    if (projectKeys.some(projectKey => !visibleProjects.includes(projectKey))) {
      setVisibleProjects(projectKeys)
    }
  }

  const setVisibleRowsValue = (newValue) => () => {
    setLocalProjectSettings(currentDisabledProjects => {
      const pageProjects = visibleProjects.reduce((previous, current) => {
        return {
          ...previous,
          [current]: newValue
        }
      }, {})

      return {
        ...currentDisabledProjects,
        ...pageProjects
      }
    })
  }

  const importLegacySettings = () => {
    const convertedSettings = legacySettings.reduce((previous, current) => {
      if (typeof current !== 'string' || !projects.find(project => project.key === current)) {
        return previous
      }

      return {
        ...previous,
        [current]: 'disabled'
      }
    }, {})

    setLocalProjectSettings(currentSettings => {
      return {
        ...currentSettings,
        ...convertedSettings
      }
    })
    setLegacySettingsImported(true)
  }

  const head = {
    cells: [
      { key: 'project', content: 'Project', isSortable: true, shouldTruncate: false },
      { key: 'enabled', content: 'Enable Example Mapping for Jira', isSortable: false, shouldTruncate: false }
    ]
  }

  const rows = projects.map(project => ({
    cells: [
      {
        key: `project-${project.key}`,
        content: <ProjectNameDisplay project={project} />
      },
      {
        key: `enabled-${project.key}`,
        content: <ProjectEnabler onChange={(event) => toggleProject(project.key, event.target.value)} value={localProjectSettings[project.key] || savedProjectSettings[project.key]} unsaved={unsavedChanges.includes(project.key)} />
      }
    ]
  }))

  return (
    <div style={{ marginLeft: 'auto', marginRight: 'auto', maxWidth: 1200, minWidth: 600, padding: 25, textAlign: 'left' }}>
      <div style={{ display: 'flex', justifyContent: 'left' }}>
        <img style={{ height: 36, width: 36, marginTop: 18, marginRight: 10, marginLeft: -8 }} src='https://cdn.teamuplabs.com/em-plugin/images/tul_logo_notext_clear.png' alt='Example Mapping for Jira logo' />
        <PageHeader>Example Mapping for Jira</PageHeader>
      </div>
      {error && <p>Something went wrong, please try again later.</p>}
      {!error &&
        <>
          {legacySettings && <div style={{ marginBottom: 20 }}><SectionMessage appearance='warning'>Your settings need to be migrated. Click the "Import legacy settings" button below to to import your old app properties. This is a one-time process to stop non-admin users from being able to change project settings. <a href='help.html#legacy-migration' target='_tulhelp' title='Read documentation'>Click here for more information.</a></SectionMessage></div>}
          <DynamicTable
            rows={rows}
            head={head}
            isLoading={loading}
            rowsPerPage={25}
            onPageRowsUpdate={onPageRowsChange}
          />
          <Button appearance='primary' isDisabled={unsavedChanges.length === 0 || saving} onClick={handleSave}>{saving ? 'Saving...' : 'Save'}</Button>
          <Button appearance='default' onClick={setVisibleRowsValue('enabled')} style={{ marginLeft: 5 }}>Enable All</Button>
          <Button appearance='default' onClick={setVisibleRowsValue('disabled')} style={{ marginLeft: 5 }}>Disable All</Button>
          {legacySettings && <Button appearance='default' onClick={importLegacySettings} isDisabled={legacySettingsImported} style={{ marginLeft: 5 }}>{legacySettingsImported ? 'Legacy Settings Imported' : 'Import legacy settings'}</Button>}
          {
            unsavedChanges.length > 0 && 
            <div style={{ display: 'inline', color: 'red', marginLeft: 5 }}>You have {unsavedChanges.length} unsaved change{unsavedChanges.length > 1 ? 's' : ''}</div>
          }
        </>
      }
      <ToastContainer
        position='bottom-center'
      />
    </div>
  )
}

export default Admin
